/* =========================================================================
   DESIGN TOKENS — Refined SaaS system (Stripe / Vercel / Linear spirit).
   Ported from design/tokens.jsx into an ES module for the CRA app.
   Three themes (dark / light / dim) share one type/space/radius scale.
   `T` is a live object; call applyTheme('dark'|'light'|'dim') to hot-swap
   color+shadow. Default = dark (per the design brief).
   ========================================================================= */

const SHARED = {
  font: {
    sans: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    mono: "'Geist Mono', ui-monospace, 'SF Mono', Menlo, monospace",
  },
  size: { micro: 10.5, caption: 12, sm: 13, base: 14, md: 15, lg: 17, xl: 20, h2: 24, stat: 30, hero: 40 },
  leading: { tight: 1.2, snug: 1.35, normal: 1.5 },
  weight: { regular: 400, medium: 500, semibold: 600, bold: 700 },
  tracking: { caps: "0.07em", wide: "0.02em", tightNeg: "-0.01em" },
  space: { 0: 0, 1: 4, 2: 6, 3: 8, 4: 12, 5: 16, 6: 20, 7: 24, 8: 32, 9: 40, 10: 48, 12: 64 },
  radius: { sm: 6, md: 8, lg: 10, xl: 14, pill: 999 },
  motion: { fast: "120ms cubic-bezier(0.2,0,0,1)", base: "180ms cubic-bezier(0.2,0,0,1)" },
};

const DARK = {
  color: {
    bg: "#0A0B0D", bgInset: "#070809",
    surface: "#101216", surfaceRaised: "#161920", surfaceHover: "#1A1E26", surfaceActive: "#1F242D",
    border: "#22262E", borderStrong: "#2E333D", borderFaint: "#191C22",
    text: "#ECEEF1", textSecondary: "#AAB1BB", textMuted: "#7A828D", textFaint: "#565D67",
    accent: "#5B8DEF", accentHover: "#6E9BF2", accentActive: "#4E7FE0", accentText: "#0A0B0D",
    accentSoft: "rgba(91,141,239,0.14)", accentSoftHi: "rgba(91,141,239,0.22)", accentRing: "rgba(91,141,239,0.45)",
    include: "#43C089", includeSoft: "rgba(67,192,137,0.13)", includeBorder: "rgba(67,192,137,0.34)",
    exclude: "#EF6B7D", excludeSoft: "rgba(239,107,125,0.13)", excludeBorder: "rgba(239,107,125,0.34)",
    review: "#EAB04F", reviewSoft: "rgba(234,176,79,0.13)", reviewBorder: "rgba(234,176,79,0.34)",
    violet: "#A88CF5", violetSoft: "rgba(168,140,245,0.14)", cyan: "#5BC8D6",
    bgImage: "radial-gradient(rgba(255,255,255,0.022) 1px, transparent 1px) 0 0 / 24px 24px, radial-gradient(135% 95% at 50% -25%, #16191F 0%, #0B0C10 52%, #07080A 100%)",
  },
  shadow: {
    e0: "none",
    e1: "0 1px 2px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.03)",
    e2: "0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)",
    e3: "0 18px 50px -12px rgba(0,0,0,0.70), 0 0 0 1px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)",
    ring: "0 0 0 3px rgba(91,141,239,0.45)",
  },
};

const LIGHT = {
  color: {
    bg: "#F5F6F8", bgInset: "#EDEFF3",
    surface: "#FFFFFF", surfaceRaised: "#FFFFFF", surfaceHover: "#F3F5F9", surfaceActive: "#ECF1FC",
    border: "#E5E8EE", borderStrong: "#D4D9E2", borderFaint: "#EEF0F4",
    text: "#191C22", textSecondary: "#4B525C", textMuted: "#6C737E", textFaint: "#99A0AB",
    accent: "#3B6FE3", accentHover: "#3463D4", accentActive: "#2C58C4", accentText: "#FFFFFF",
    accentSoft: "rgba(59,111,227,0.09)", accentSoftHi: "rgba(59,111,227,0.15)", accentRing: "rgba(59,111,227,0.32)",
    include: "#1B9D5F", includeSoft: "rgba(27,157,95,0.11)", includeBorder: "rgba(27,157,95,0.30)",
    exclude: "#D8425A", excludeSoft: "rgba(216,66,90,0.10)", excludeBorder: "rgba(216,66,90,0.30)",
    review: "#B4791C", reviewSoft: "rgba(180,121,28,0.12)", reviewBorder: "rgba(180,121,28,0.32)",
    violet: "#7A57D1", violetSoft: "rgba(122,87,209,0.11)", cyan: "#16838F",
    bgImage: "radial-gradient(rgba(40,52,84,0.028) 1px, transparent 1px) 0 0 / 24px 24px, radial-gradient(135% 95% at 50% -25%, #FFFFFF 0%, #F4F6F9 52%, #EAEDF2 100%)",
  },
  shadow: {
    e0: "none",
    e1: "0 1px 2px rgba(16,24,40,0.06), 0 1px 1px rgba(16,24,40,0.04)",
    e2: "0 4px 16px -2px rgba(16,24,40,0.10), 0 2px 6px -1px rgba(16,24,40,0.06)",
    e3: "0 24px 56px -16px rgba(16,24,40,0.28), 0 0 0 1px rgba(16,24,40,0.04)",
    ring: "0 0 0 3px rgba(59,111,227,0.30)",
  },
};

const DIM = {
  color: {
    bg: "#EAECF0", bgInset: "#DDE0E7",
    surface: "#FBFCFD", surfaceRaised: "#FFFFFF", surfaceHover: "#F0F2F7", surfaceActive: "#E2E9F8",
    border: "#D3D7E0", borderStrong: "#C0C6D1", borderFaint: "#E8EBF0",
    text: "#20242C", textSecondary: "#4D545F", textMuted: "#6D7480", textFaint: "#959CA8",
    accent: "#3866DC", accentHover: "#315ACB", accentActive: "#2A50BC", accentText: "#FFFFFF",
    accentSoft: "rgba(56,102,220,0.10)", accentSoftHi: "rgba(56,102,220,0.16)", accentRing: "rgba(56,102,220,0.32)",
    include: "#198F58", includeSoft: "rgba(25,143,88,0.12)", includeBorder: "rgba(25,143,88,0.32)",
    exclude: "#CE3E55", excludeSoft: "rgba(206,62,85,0.11)", excludeBorder: "rgba(206,62,85,0.32)",
    review: "#A66E18", reviewSoft: "rgba(166,110,24,0.13)", reviewBorder: "rgba(166,110,24,0.34)",
    violet: "#7150C6", violetSoft: "rgba(113,80,198,0.12)", cyan: "#147A85",
    bgImage: "radial-gradient(rgba(46,60,96,0.045) 1px, transparent 1px) 0 0 / 22px 22px, radial-gradient(140% 110% at 50% -25%, #F6F7FB 0%, #E9EBF1 46%, #DBDFE8 100%)",
  },
  shadow: {
    e0: "none",
    e1: "0 1px 2px rgba(28,36,54,0.08), 0 1px 1px rgba(28,36,54,0.04)",
    e2: "0 4px 16px -3px rgba(28,36,54,0.12), 0 2px 6px -1px rgba(28,36,54,0.07)",
    e3: "0 24px 56px -16px rgba(20,28,44,0.32), 0 0 0 1px rgba(28,36,54,0.05)",
    ring: "0 0 0 3px rgba(56,102,220,0.30)",
  },
};

export const THEMES = { dark: DARK, light: LIGHT, dim: DIM };

const DEFAULT_MODE = "light"; // [D8 2026-06-10] Blake landed on LIGHT in design; dark/dim available via applyTheme
export const T = { ...SHARED, color: THEMES[DEFAULT_MODE].color, shadow: THEMES[DEFAULT_MODE].shadow, _mode: DEFAULT_MODE };

export function applyTheme(mode) {
  const th = THEMES[mode] || DARK;
  T.color = th.color;
  T.shadow = th.shadow;
  T._mode = mode;
  if (typeof document !== "undefined" && document.body) {
    document.body.style.background = th.color.bgImage || th.color.bg;
    document.body.style.backgroundColor = th.color.bg;
    document.body.style.backgroundAttachment = "fixed";
    document.body.setAttribute("data-theme", mode);
  }
  return T;
}
